import { BarChart3, BellRing, Eye, EyeOff, LayoutDashboard, List } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { useAuth } from './useAuth';

const featureCards = [
  { icon: BarChart3, title: 'Advanced charts' },
  { icon: List, title: 'Live watchlists' },
  { icon: BellRing, title: 'Price alerts' },
  { icon: LayoutDashboard, title: 'Sync layouts' },
] as const;

const cardClassName = 'border border-transparent bg-[linear-gradient(rgba(24,24,27,0.82),rgba(24,24,27,0.82)),linear-gradient(135deg,rgba(255,255,255,0.16),rgba(255,255,255,0.02))] bg-origin-border bg-clip-padding [background-clip:padding-box,border-box] shadow-[0_20px_80px_rgba(0,0,0,0.42)] backdrop-blur-xl';

export function AuthPanel() {
  const { loading, status, error, signIn, signUp, clearMessage } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [showPassword, setShowPassword] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mode === 'signin') {
      await signIn(username.trim(), password);
      return;
    }

    await signUp(username.trim(), email.trim(), password);
  }

  return (
    <main className="relative grid min-h-dvh w-full place-items-center overflow-x-hidden overflow-y-auto bg-black px-5 py-7 font-['Inter',ui-sans-serif,system-ui,sans-serif] text-zinc-100 selection:bg-cyan-300 selection:text-zinc-950 sm:px-6 sm:py-8 lg:px-8">
      <div className="auth-circuit-field absolute inset-0" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_15%,rgba(34,211,238,0.18),transparent_34%),linear-gradient(90deg,#000_0%,rgba(0,0,0,0.92)_36%,rgba(0,0,0,0.66)_70%,#000_100%)]" />
      <div className="relative z-10 mx-auto w-full max-w-6xl">
        <div className="hidden items-center gap-6 md:grid md:grid-cols-3 lg:gap-8">
          <div className="space-y-6">
            {featureCards.slice(0, 2).map((feature) => (
              <FeatureCard key={feature.title} {...feature} desktop />
            ))}
          </div>

          <div className={cn(cardClassName, 'rounded-[2px] p-8 lg:p-10')}>
            <AuthForm
              username={username}
              email={email}
              password={password}
              mode={mode}
              loading={loading}
              status={status}
              error={error}
              showPassword={showPassword}
              setUsername={setUsername}
              setEmail={setEmail}
              setPassword={setPassword}
              setMode={setMode}
              setShowPassword={setShowPassword}
              clearMessage={clearMessage}
              onSubmit={submit}
            />
          </div>

          <div className="space-y-6">
            {featureCards.slice(2).map((feature) => (
              <FeatureCard key={feature.title} {...feature} desktop />
            ))}
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-[320px] flex-col items-center gap-4 md:hidden">
          <div className={cn(cardClassName, 'w-full rounded-[2px] px-4 py-5')}>
            <AuthForm
              username={username}
              email={email}
              password={password}
              mode={mode}
              loading={loading}
              status={status}
              error={error}
              showPassword={showPassword}
              setUsername={setUsername}
              setEmail={setEmail}
              setPassword={setPassword}
              setMode={setMode}
              setShowPassword={setShowPassword}
              clearMessage={clearMessage}
              onSubmit={submit}
            />
          </div>

          <div className="grid w-full grid-cols-2 gap-3">
            {featureCards.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}

type AuthFormProps = {
  username: string;
  email: string;
  password: string;
  mode: 'signin' | 'signup';
  loading: boolean;
  status: string;
  error: string;
  showPassword: boolean;
  setUsername(value: string): void;
  setEmail(value: string): void;
  setPassword(value: string): void;
  setMode(value: 'signin' | 'signup'): void;
  setShowPassword(value: boolean): void;
  clearMessage(): void;
  onSubmit(event: FormEvent<HTMLFormElement>): void;
};

function AuthForm({ username, email, password, mode, loading, status, error, showPassword, setUsername, setEmail, setPassword, setMode, setShowPassword, clearMessage, onSubmit }: AuthFormProps) {
  const isSignIn = mode === 'signin';

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="space-y-2 text-center">
        <div className="mb-2 flex items-center justify-center gap-2 md:mb-4">
          <BarChart3 className="h-4 w-4 text-cyan-200 drop-shadow-[0_0_10px_rgba(103,232,249,0.45)] md:h-6 md:w-6" />
          <span className="font-mono text-[15px] font-bold tracking-tight text-cyan-200 md:text-lg">CHARTIFY</span>
        </div>
        <h1 className="bg-gradient-to-r from-zinc-100 via-cyan-100 to-indigo-200 bg-clip-text text-[24px] leading-tight font-bold tracking-[-0.035em] text-transparent md:text-2xl">{isSignIn ? 'Welcome back' : 'Create account'}</h1>
        <p className="text-[13px] leading-5 text-zinc-300 md:text-sm">
          {isSignIn ? 'Log in to access your dashboard.' : 'Create your account, then sign in to access your dashboard.'}
        </p>
      </div>

      <form className="space-y-3 md:space-y-4" onSubmit={onSubmit}>
        <Input
          id="login-username"
          type="text"
          inputMode="text"
          placeholder={isSignIn ? 'Username or email' : 'Username'}
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={username}
          onChange={(event) => {
            setUsername(event.target.value);
            clearMessage();
          }}
          disabled={loading}
          className="h-10 rounded-[4px] border-white/15 bg-zinc-950/55 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus-visible:border-cyan-200/70 focus-visible:ring-cyan-300/20 md:h-12 md:px-4 md:py-3 md:text-base"
        />

        {!isSignIn && (
          <Input
            id="signup-email"
            type="email"
            inputMode="email"
            placeholder="Email address"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              clearMessage();
            }}
            disabled={loading}
            className="h-10 rounded-[4px] border-white/15 bg-zinc-950/55 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus-visible:border-cyan-200/70 focus-visible:ring-cyan-300/20 md:h-12 md:px-4 md:py-3 md:text-base"
          />
        )}

        <div className="relative">
          <Input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            autoComplete={isSignIn ? 'current-password' : 'new-password'}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              clearMessage();
            }}
            disabled={loading}
            className="h-10 rounded-[4px] border-white/15 bg-zinc-950/55 px-3 py-2 pr-10 text-sm text-zinc-100 placeholder:text-zinc-500 focus-visible:border-cyan-200/70 focus-visible:ring-cyan-300/20 md:h-12 md:px-4 md:py-3 md:pr-12 md:text-base"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute top-1/2 right-3 -translate-y-1/2 text-zinc-500 transition hover:text-cyan-100 disabled:cursor-not-allowed disabled:opacity-50 md:right-4"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            disabled={loading}
          >
            {showPassword ? <EyeOff className="h-4 w-4 md:h-5 md:w-5" /> : <Eye className="h-4 w-4 md:h-5 md:w-5" />}
          </button>
        </div>

        <button
          type="submit"
          className="inline-flex w-full items-center justify-center rounded-[4px] bg-cyan-300 py-2.5 text-sm font-semibold tracking-[0.12em] text-zinc-950 uppercase shadow-[0_0_0_1px_rgba(255,255,255,0.18)_inset] transition-all hover:shadow-[0_0_18px_rgba(103,232,249,0.55)] disabled:cursor-not-allowed disabled:opacity-50 md:py-3 md:text-base"
          disabled={loading}
        >
          {isSignIn ? 'Sign In' : 'Sign Up'}
        </button>
      </form>

      <div className="space-y-3 text-center text-[13px] md:text-sm">
        {isSignIn ? (
          <div className="text-zinc-300">
            Don&apos;t have an account?{' '}
            <button
              type="button"
              className="font-semibold text-cyan-200 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => {
                setMode('signup');
                clearMessage();
              }}
              disabled={loading}
            >
              Sign up
            </button>
          </div>
        ) : (
          <div className="text-zinc-300">
            Already have an account?{' '}
            <button
              type="button"
              className="font-semibold text-cyan-200 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => {
                setMode('signin');
                clearMessage();
              }}
              disabled={loading}
            >
              Sign in
            </button>
          </div>
        )}
      </div>

      {(error || status) && (
        <div className={cn('text-center text-[13px] text-cyan-200 md:text-sm', error && 'text-[var(--danger)]')}>{error || status}</div>
      )}
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desktop = false }: (typeof featureCards)[number] & { desktop?: boolean }) {
  return (
    <div className={cn(cardClassName, 'grid min-h-[82px] place-items-center rounded-[2px] p-3 text-center transition hover:shadow-[0_0_18px_rgba(103,232,249,0.18)]', desktop && 'min-h-0 p-6')}>
      <div>
        <Icon className={cn('mx-auto mb-1.5 h-4 w-4 text-cyan-200', desktop && 'mb-3 h-8 w-8')} />
        <p className={cn('font-mono text-[11px] leading-4 font-bold tracking-[0.16em] text-zinc-300 uppercase', desktop && 'text-xs')}>{title}</p>
      </div>
    </div>
  );
}
